import { useMemo, useState } from "react";
import type { AiMode, AiModel } from "@media-viewer/shared";
import { Modal } from "../Modal";

interface ModelPickerProps {
  open: boolean;
  onClose: () => void;
  models: AiModel[];
  mode: AiMode;
  exampleCount: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function priceLabel(m: AiModel): string {
  const { minCredits, maxCredits } = m.priceHint;
  return minCredits === maxCredits ? String(minCredits) : `${minCredits}–${maxCredits}`;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export function ModelPicker({ open, onClose, models, mode, exampleCount, selectedId, onSelect }: ModelPickerProps) {
  const [search, setSearch] = useState("");
  const [cheapest, setCheapest] = useState(false);

  const list = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const out = models.filter(
      (m) => m.mode === mode && (!needle || m.name.toLowerCase().includes(needle) || m.id.toLowerCase().includes(needle)),
    );
    if (cheapest) out.sort((a, b) => a.priceHint.minCredits - b.priceHint.minCredits);
    return out;
  }, [models, mode, search, cheapest]);

  return (
    <Modal open={open} onClose={onClose} title="Choose a model" wide>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="model-search" className="sr-only">
          Search models
        </label>
        <input
          id="model-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search models"
          autoFocus
          className="min-w-0 flex-1 rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-zinc-200"
        />
        <button
          aria-pressed={cheapest}
          onClick={() => setCheapest((v) => !v)}
          className={
            "rounded px-3 py-1.5 text-xs " +
            (cheapest ? "bg-zinc-700 text-zinc-100" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200")
          }
        >
          Cheapest first
        </button>
      </div>
      <p className="mt-2 text-xs text-zinc-500">
        {mode === "edit" ? "Showing models that accept example images." : "Showing models that generate from a prompt."}{" "}
        Prices are Venice's current prices in credits (1 credit = $0.01).
      </p>
      <ul className="mt-3 max-h-[60vh] space-y-1.5 overflow-auto">
        {list.length === 0 && <li className="py-6 text-center text-zinc-500">No models match.</li>}
        {list.map((m) => {
          const tooMany = mode === "edit" && m.maxInputImages < exampleCount;
          const selected = m.id === selectedId;
          return (
            <li key={m.id}>
              <button
                disabled={tooMany}
                onClick={() => {
                  onSelect(m.id);
                  onClose();
                }}
                className={
                  "flex w-full items-center justify-between gap-3 rounded border px-3 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50 " +
                  (selected
                    ? "border-zinc-500 bg-zinc-800"
                    : "border-zinc-800 bg-zinc-950 hover:border-zinc-600 disabled:hover:border-zinc-800")
                }
              >
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-medium text-zinc-100">{m.name}</span>
                  {tooMany ? (
                    <span className="text-xs text-amber-300">
                      Takes {plural(m.maxInputImages, "image")} — remove{" "}
                      {plural(exampleCount - m.maxInputImages, "example image")} to use it
                    </span>
                  ) : (
                    <span className="flex flex-wrap gap-1.5 text-xs text-zinc-400">
                      {mode === "edit" && <span>up to {plural(m.maxInputImages, "image")}</span>}
                      {m.traits.map((t) => (
                        <span key={t} className="rounded bg-zinc-800 px-1.5 text-[11px] text-zinc-300">
                          {t.replace(/_/g, " ")}
                        </span>
                      ))}
                      {m.deprecated && <span className="text-amber-300">retiring soon</span>}
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 flex-col items-end">
                  <span className="font-medium tabular-nums text-zinc-100">{priceLabel(m)}</span>
                  <span className="text-[11px] text-zinc-500">credits</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
